'use client';

import { Button } from '@components/Button';
import { CheckBox, InputField } from '@components/FormFields';
import RichText from '@components/RichText';
import SecondaryNavigation from '@components/secondaryNavBar';
import { useSnackbar } from '@components/Snackbar';
import { BodyCopy } from '@components/Typography';
import { Document } from '@contentful/rich-text-types';
import { getContentTypePageData, getWelcomeMessageDescription } from '@data/page';
import { getEntries } from '@data/preview';
import { color, font, query, space } from '@design/templateFns';
import { CreateLoftyLeadDto } from '@engelvoelkersamericas/api';
import auth from '@util/api/auth/index';
import { allCurrenciesOptions } from '@util/convertGivernCurrencytoBaseCurrency';
import isObjectEmpty from '@util/isObjectEmpty';
import { getCurrentLocale } from '@util/locale';
import { NavSection } from '@util/shopNav';
import { SelectOption } from '@util/types';
import { apiClient } from 'api-client';
import i18nConfig from 'i18n.config';
import { observer } from 'mobx-react-lite';
import { MeasurementUnit } from 'mobx/stores/app-store';
import { useStores } from 'mobx/useStores';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { usePostHog } from 'posthog-js/react';
import { FC, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Logo } from '../Logo';
import { Signet } from '../Logo/Signet';
import Modal from '../Modal';
import { useGlobalSiteHeaderContext } from './GlobalSiteHeaderContext';
import { Headline, List, ListCol, Text } from './styles';

export interface CustomDropdownProps {
  value: string | null;
  options: SelectOption<string | null>[];
  onSelect: (value: string) => void;
  open?: boolean;
  testIdType?: string;
}

const CustomDropdown: FC<CustomDropdownProps> = ({
  value,
  options,
  onSelect,
  open = false,
  testIdType,
}) => {
  const [val, setVal] = useState(value);
  const dropdown = useRef<any | null>(null);

  const isOpen = () => !dropdown.current?.hasAttribute('open');
  const select = (e, item) => {
    e.stopPropagation();
    dropdown.current.removeAttribute('open');
    setVal(item.value);
    onSelect(item.value);
  };
  useEffect(() => {
    if (open) dropdown.current.setAttribute('open', 'true');
  }, [dropdown]);
  return (
    <SelectDetails
      data-testid={`${testIdType || 'section'}-dropdown`}
      onClick={isOpen}
      ref={dropdown}
    >
      <summary data-testid={`${testIdType || 'dropdown'}-selected-value`}>
        <Text $p="0px">{options.find((o) => o.value == val)?.title}</Text>
      </summary>
      <div className="options" data-testid={`${testIdType || 'dropdown'}-options`}>
        {options.map((item) => (
          <OptionsList
            data-testid={`${item.value}-option`}
            key={`select-option-${value}`}
            $isActive={item.value === val}
          >
            <Text
              $p="0px"
              onClick={(e) => select(e, item)}
              key={item.value}
              data-testid={item.value}
            >
              {item.title}
            </Text>
          </OptionsList>
        ))}
      </div>
    </SelectDetails>
  );
};
interface SecondaryNav {
  topLevelNav?: NavSection[];
  mobileNav?: NavSection[];
  allowedSlugs?: string[];
}

interface GlobalSiteHeaderProps {
  nav: NavSection[];
}

const GlobalSiteHeader: FC<GlobalSiteHeaderProps> = observer(({ nav }) => {
  const t = useTranslations();
  const { appStore, shopStore } = useStores();
  const { urlDomainOverride, validSubdomainData } = appStore;
  const { shopChimeId } = shopStore;
  const locale = getCurrentLocale();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const posthog = usePostHog();
  const [secondaryNav, setSecondaryNav] = useState<SecondaryNav | null>(null);

  const { topLevelNav, mobileNav, allowedSlugs } = secondaryNav ?? {};

  const getSecondaryNavBar = async () => {
    const slug = pathname.substring(1).replace(`${locale}/`, '');
    const pageData = await getContentTypePageData(
      locale,
      slug,
      'page',
      false,
      'secondaryPageNavigation',
    );

    const secondaryNavBar = pageData?.items?.[0]?.fields?.secondaryPageNavigation?.fields;
    if (secondaryNavBar) {
      setSecondaryNav(secondaryNavBar);
    }
  };

  useEffect(() => {
    getSecondaryNavBar();
  }, [pathname]);

  const languagesOptions: SelectOption[] = [
    { value: 'en', title: 'English' },
    { value: 'fr-CA', title: 'French (Canada)' },
    { value: 'es', title: 'Spanish' },
  ];

  const measurementOptions: SelectOption<MeasurementUnit>[] = [
    {
      value: 'sq.ft',
      title: t('Square Feet'),
    },
    {
      value: 'm²',
      title: t('Square Meters'),
    },
  ];

  const { modalSection, toSetModalSection } = useGlobalSiteHeaderContext();
  const { preferredCurrency, setPreferredCurrency, preferredMeasurement, setPreferredMeasurement } =
    appStore;
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [userFirstName, setUserFirstName] = useState('');
  const [userLastName, setUserLastName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userPhone, setUserPhone] = useState('');
  const [loginError, setLoginError] = useState<{ type?: string; msg: string } | null>(null);
  const [verifyCodeError, setVerifyCodeError] = useState<{ type?: string; msg: string } | null>(
    null,
  );
  const [forgotPassError, setForgotPassError] = useState('');
  const [user, setUser] = useState<any | null>(null);
  const [lang, setLang] = useState<string>(getCurrentLocale());
  const [welcomeMessageDescription, setWelcomeMessageDescription] = useState(null);
  const [isfpCodeSent, setIsfpCodeSent] = useState(false);
  const [registrationCode, setRegistrationCode] = useState('');
  const [fpVerificationCode, setfpVerificationCode] = useState('');
  const [rewriteBaseUrl, setRewriteBaseUrl] = useState('');
  const { showSnackbar } = useSnackbar();
  let modal = useRef<any>(null);
  let languageModal = useRef<any>(null);
  let mobileLanguageModal = useRef<any>(null);
  let mobileCurrencyModal = useRef<any>(null);
  let mobileMeasurementModal = useRef<any>(null);

  const logout = async () => {
    await auth.signOut();
    posthog.reset();
    setUser(null);
    toSetModalSection('accountIntro');
    closeAllModals();
    router.push('/');
  };
  //Get Site Modal Data
  const americasSiteModalContentfulId = '5TZzffpltvc7predmzU8aw';

  const fetchAmericasSiteModalData = async () => {
    try {
      let componentData = await getEntries(americasSiteModalContentfulId, lang);
      return componentData;
    } catch (e) {
      console.log(e);
    }
  };
  const [siteModalData, setSiteModalData] = useState<any>({});
  const [showInnerMobileSecondaryNav, setShowInnerMobileSecondaryNav] = useState(false);
  const [navMobileProps, setNavMobileProps] = useState(nav);
  useEffect(() => {
    fetchAmericasSiteModalData().then((data) => {
      setSiteModalData(data.fields);
    });
  }, []);

  useEffect(() => {
    if (showInnerMobileSecondaryNav && topLevelNav) {
      // Inner mobile nav shows same items as top level secondary mav
      setNavMobileProps(topLevelNav);
    } else {
      if (mobileNav) {
        // This is for outer mobile nav (links shown originally on mobile nav)
        setNavMobileProps([...nav, ...mobileNav]);
      } else {
        setNavMobileProps(nav);
      }
    }
  }, [showInnerMobileSecondaryNav, topLevelNav, mobileNav]);

  const changeLang = async (locale: string) => {
    // set cookie for next-i18n-router
    const days = 30;
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    const expires = '; expires=' + date.toUTCString();
    document.cookie = `NEXT_LOCALE=${locale};expires=${expires};path=/`;

    const params = searchParams.toString();
    const pathnameWithParams = params ? `${pathname}?${params}` : pathname;

    // redirect to the new locale path
    if (locale === i18nConfig.defaultLocale && !i18nConfig.prefixDefault) {
      router.push(pathnameWithParams);
    } else if (pathnameWithParams.includes(`/${lang}`)) {
      router.push(pathnameWithParams.replace(`/${lang}`, `/${locale}`));
    } else {
      router.push(`/${locale}/${pathnameWithParams}`);
    }

    setLang(locale);
    router.refresh();
  };

  const handleClick = () => {
    toSetModalSection('menu');
    modal.current.toggle(true);
    document.body.style.overflow = 'hidden';
  };
  const closeAllModals = () => {
    modal.current.toggle(false);
    languageModal.current.toggle(false);
    mobileLanguageModal.current.toggle(false);
    mobileCurrencyModal.current.toggle(false);
    mobileMeasurementModal.current.toggle(false);
    document.body.style.overflow = '';

    setTimeout(() => {
      toSetModalSection('menu');
    }, 500); // wait for modal to close
  };
  const handleLanguageClick = () => {
    languageModal.current.toggle(true);
  };
  const handleMobileLanguageClick = () => {
    mobileLanguageModal.current.toggle(true);
  };
  const handleMobileCurrencyClick = () => {
    mobileCurrencyModal.current.toggle(true);
  };
  const handleMobileMeasurementClick = () => {
    mobileMeasurementModal.current.toggle(true);
  };
  const handleAccountClick = () => {
    toSetModalSection('account');
    modal.current.toggle(true);
  };
  const handleInputChange = (event, func) => {
    let val = event.target.value;
    func(val);
  };
  const registerUser = async () => {
    try {
      let phoneRegexE164 = /^\+[1-9]\d{1,14}$/;
      setLoginError(null);
      if (userFirstName == '')
        return setLoginError({
          type: 'firstName',
          msg: t('Validation.firstNameRequired'),
        });
      if (userLastName == '')
        return setLoginError({
          type: 'lastName',
          msg: t('Validation.lastNameRequired'),
        });
      if (userPhone == '')
        return setLoginError({
          type: 'phone',
          msg: t('Validation.phoneNumberRequired'),
        });
      if (phoneRegexE164.test(userPhone) == false)
        return setLoginError({
          type: 'phone',
          msg: t('Validation.phoneNumberInvalid'),
        });
      if (userEmail == '')
        return setLoginError({
          type: 'email',
          msg: t('Validation.emailRequired'),
        });
      if (!isEmailValid(userEmail)) {
        return setLoginError({
          type: 'email',
          msg: t('Validation.emailInvalid'),
        });
      }
      if (password == '')
        return setLoginError({
          type: 'password',
          msg: t('Validation.passwordRequired'),
        });
      if (!isPasswordValid(password)) {
        return setLoginError({
          type: 'password',
          msg: t('Validation.passwordInvalid'),
        });
      }
      if (confirmPassword != password)
        return setLoginError({
          type: 'confirmPassword',
          msg: t('Validation.passwordMismatch'),
        });
      const res: any = await auth.register({
        userFirstName,
        userLastName,
        userEmail,
        userPhone,
        username: userEmail,
        password,
      });

      if (res.error) {
        throw res.error;
      }

      // ask for confirmation code
      setUsername(userEmail);
      toSetModalSection('verifyRegistration');
      // login now...
    } catch (err) {
      let userFriendlyMessage = getIncognitoUserFriendlyMessagesByCode(err);
      setLoginError({ type: 'unknown', msg: userFriendlyMessage });
    }
  };
  const login = async () => {
    try {
      setLoginError(null);
      const user = await auth.login({
        username,
        password,
      });

      if (user) {
        setUserDetailsIfUserIsAlreadyLogin(user);
      }
      closeAllModals();
      router.push('/dashboard/account-menu');
    } catch (err) {
      console.log(err);
      if ((err as any).toString().startsWith('UserNotConfirmedException')) {
        toSetModalSection('verifyRegistration');
        resendRegistrationCode();
      }

      if (siteModalData.emailOrPasswordInvalidMessage) {
        setLoginError({ msg: siteModalData.emailOrPasswordInvalidMessage });
      } else {
        setLoginError({ msg: 'Error: Email or password is incorrect.' });
      }
    }
    // modal.current.toggle(true);
  };

  const openModalSection = (section) => {
    setLoginError(null);
    toSetModalSection(section);
    modal.current.setOpen(true);
  };

  const setUserDetailsIfUserIsAlreadyLogin = async (userData?: any | null) => {
    try {
      const locale = getCurrentLocale();
      if (isObjectEmpty(userData)) {
        userData = await auth.getUser();
      }

      if (!isObjectEmpty(userData)) {
        const userId = userData.attributes.sub;
        posthog.identify(userId, { email: userData.attributes.email });

        const welcomeMessageDesc = await getWelcomeMessageDescription(
          '1jAbfEglI5vbf0KVLcwkEY',
          locale,
        );
        setUser(userData);
        toSetModalSection('welcomeMessage');
        if (welcomeMessageDesc) setWelcomeMessageDescription(welcomeMessageDesc.fields.description);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const redirectToDashbaord = () => {
    if (!isObjectEmpty(user)) {
      router.push('/dashboard/account-menu');
    }
  };

  useEffect(() => {
    setUserDetailsIfUserIsAlreadyLogin();
  }, []);

  useEffect(() => {
    if (modalSection == 'accountIntro') modal.current.setOpen(true);
    if (
      modalSection === 'account' ||
      modalSection === 'forgotPassword' ||
      modalSection === 'createAccount'
    ) {
      removeInternalStateData();
    }
  }, [modalSection]);

  const isEmailValid = (email: string): boolean => {
    let hasSpacesRegex = /\s/g;
    let emailRegexRFC5322 =
      /([!#-'*+/-9=?A-Z^-~-]+(\.[!#-'*+/-9=?A-Z^-~-]+)*|"([]!#-[^-~ \t]|(\\[\t -~]))+")@([!#-'*+/-9=?A-Z^-~-]+(\.[!#-'*+/-9=?A-Z^-~-]+)*|\[[\t -Z^-~]*])/;
    if (!email || emailRegexRFC5322.test(email) == false || hasSpacesRegex.test(email) == true) {
      return false;
    }
    return true;
  };

  const isPasswordValid = (password: string): boolean => {
    let passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

    if (!password || passwordRegex.test(password) == false) {
      return false;
    }
    return true;
  };

  const sendForgotPasswordCode = async () => {
    try {
      setForgotPassError('');
      if (!isEmailValid(username)) {
        setForgotPassError(
          siteModalData.verificationCodeErrorMessage
            ? siteModalData.verificationCodeErrorMessage
            : 'Error: Invalid email address. Please check your formatting.',
        );
        return;
      }
      const res = await auth.sendForgotPasswordCode(username);
      if (!isObjectEmpty(res?.nextStep)) {
        setIsfpCodeSent(true);
        showSnackbar(
          siteModalData.verificationCodeSentMessage
            ? siteModalData.verificationCodeSentMessage
            : 'Verification code sent to email',
        );
      }
    } catch (err) {
      if ((err as any).log) {
        return (err as any).log;
      }

      const c = (err as any).code || (err as any).name;
      switch (c) {
        case 'InvalidParameterException':
          setForgotPassError(
            `Error: In order to reset your password, you must first verify your email.`,
          );
          toSetModalSection('verifyRegistration');
          resendRegistrationCode();
          break;
        default:
          setForgotPassError(`Error: ${(err as Error).message}`);
      }
    }
  };

  const resetPasswordWithVerificationCode = async () => {
    try {
      setForgotPassError('');
      if (!isEmailValid(username)) {
        setForgotPassError(
          siteModalData.verificationCodeErrorMessage
            ? siteModalData.verificationCodeErrorMessage
            : 'Error: Invalid email address. Please check your formatting.',
        );
        return;
      } else if (fpVerificationCode?.trim() == '') {
        setForgotPassError(
          siteModalData.verificationCodeErrorMessage
            ? siteModalData.verificationCodeErrorMessage
            : 'Error: Verification code cannot be empty.',
        );
        return;
      } else if (password == '') {
        setForgotPassError(
          siteModalData.emptyPasswordMessage
            ? siteModalData.emptyPasswordMessage
            : 'Error: Password cannot be empty.',
        );
        return;
      } else if (!isPasswordValid(password)) {
        setForgotPassError(
          'Password must contain at least one special character, number, uppercase and lowercase letter and be between 8 and 32 characters long.',
        );
        return;
      } else if (password != confirmPassword) {
        setForgotPassError(
          siteModalData.confirmPasswordMismatch
            ? siteModalData.confirmPasswordMismatch
            : 'Error: Confirm password does not match password.',
        );
        return;
      }
      const res = await auth.resetPassword({
        username,
        new_password: password,
        code: fpVerificationCode,
      });
      showSnackbar(
        siteModalData.resetPasswordMessage
          ? siteModalData.resetPasswordMessage
          : 'Password reset successfully',
      );
      toSetModalSection('account');
    } catch (err) {
      setForgotPassError(`Error: ${(err as Error).message}`);
    }
  };

  const removeInternalStateData = () => {
    setUsername('');
    setPassword('');
    setConfirmPassword('');
    setUserFirstName('');
    setUserLastName('');
    setUserEmail('');
    setUserPhone('');
    setLoginError(null);
    setForgotPassError('');
    setIsfpCodeSent(false);
    setfpVerificationCode('');
  };

  const getIncognitoUserFriendlyMessagesByCode = (err: any) => {
    let c;
    if (typeof err == 'object') {
      if (err.log) return err.log;
      c = err.code || err.name;
    }
    if (c) {
      let userFriendlyMessage = '';
      switch (c) {
        case 'UsernameExistsException':
          userFriendlyMessage = siteModalData
            ? siteModalData.usernameExistsException
            : 'This user already exists. Please log in with your password.';
          break;
        case 'InvalidPasswordException':
          userFriendlyMessage = siteModalData
            ? siteModalData.invalidPasswordException
            : 'Your password is invalid. Password must be 8 characters long and have at least one uppercase and one lowercase letter.';
          break;
        case 'InvalidParameterException':
          userFriendlyMessage = siteModalData
            ? siteModalData.invalidParameterException
            : 'A required parameter is missing or invalid during the sign-up attempt. Please ensure all fields are filled out correctly.';
          break;
        case 'TooManyRequestsException':
          userFriendlyMessage = siteModalData
            ? siteModalData.ooManyRequestsException
            : 'Too many attempts. Please try again later.';
          break;
        case 'ResourceNotFoundException':
          userFriendlyMessage = siteModalData
            ? siteModalData.resourceNotFoundException
            : 'The resource, typically the user pool, was not found. Please contact support.';
          break;
        case 'NotAuthorizedException':
          userFriendlyMessage = siteModalData
            ? siteModalData.notAuthorizedException
            : "You're not authorized to perform this action. Please check your credentials.";
          break;
        case 'CodeMismatchException':
          userFriendlyMessage = siteModalData
            ? siteModalData.codeMismatchException
            : 'Invalid verification code provided. Please check and try again.';
          break;
        case 'UserNotConfirmedException':
          userFriendlyMessage = siteModalData
            ? siteModalData.userNotConfirmedException
            : 'The user is not confirmed. Please verify your account.';
          break;
        case 'UserNotFoundException':
          userFriendlyMessage = siteModalData
            ? siteModalData.userNotFoundException
            : 'User does not exist. Please check your credentials.';
          break;
        case 'NetworkError':
          userFriendlyMessage = siteModalData
            ? siteModalData.networkError
            : 'Network error. Please check your internet connection and try again.';
          break;
        default:
          userFriendlyMessage = siteModalData
            ? siteModalData.defaultRegistrationExcemption
            : 'An error occurred during registration. Please try again.';
          break;
      }
      return userFriendlyMessage;
    }
    return siteModalData.defaultError ? siteModalData.defaultError : 'Error: Something went wrong';
  };

  const verifyRegistrationCode = async () => {
    try {
      const res = await auth.confirmSignUpCode({
        username: username,
        code: registrationCode,
      });
      const user = await auth.login({
        username: username,
        password: password,
      });
      setUserDetailsIfUserIsAlreadyLogin(user);
      posthog.capture('user_registered', { email: user.attributes.email });
      // Send the data to the server in JSON format.
      const chimeObj: CreateLoftyLeadDto = {
        userFirstName,
        userLastName,
        userPhone,
        userEmail,
        chimeId: 0,
        type: 'EV Global',
      };
      if (shopChimeId) {
        chimeObj.ownershipId = shopChimeId;
        chimeObj.chimeId = shopChimeId;
        chimeObj.ownershipScope = 'OFFICE';
        chimeObj.type = 'Shop';
      }
      await apiClient.createLoftyLeads({
        createLoftyLeadDto: chimeObj,
      });
    } catch (err) {
      let userFriendlyMessage = getIncognitoUserFriendlyMessagesByCode((err as any).error);
      setVerifyCodeError({ msg: userFriendlyMessage });
    }
  };
  const resendRegistrationCode = async () => {
    try {
      let res = await auth.resendConfirmationCode({
        username: username,
      });
      console.log('Resend Sign Up Code', res);
    } catch (err) {
      setVerifyCodeError(getIncognitoUserFriendlyMessagesByCode((err as any).error));
    }
  };

  const getLabelBySelectedOption = (
    selectedValue: string | null,
    options: SelectOption<string | null>[],
  ) => {
    const result = options.find((mObj) => mObj.value === selectedValue);
    return result ? result.title : null;
  };

  const secondaryNavData = mobileNav?.[0]?.fields;

  return (
    <>
      {topLevelNav && (
        <SecondaryNavigation topLevelNav={topLevelNav} allowedSlugs={allowedSlugs ?? []} />
      )}
      <Modal
        disableContainerToggle={
          modalSection !== 'welcomeMessage' && modalSection !== 'accountIntro'
        }
        top={
          <div>
            <TopDefault>
              {modalSection == 'menu' && !showInnerMobileSecondaryNav && mobileNav ? (
                <Signet size="48px" />
              ) : (
                mobileNav &&
                showInnerMobileSecondaryNav && (
                  <>
                    <Button
                      variant="iconOnly"
                      arrow={false}
                      icon="ArrowLeft"
                      label="Left Arrow"
                      iconSize="36px"
                      onClickHandler={() => setShowInnerMobileSecondaryNav(false)}
                    />
                    <MainMenuText style={{ paddingBottom: 0 }}>Main Menu</MainMenuText>
                  </>
                )
              )}
              {modalSection != 'menu' && (
                <>
                  <Label onClick={() => toSetModalSection('menu')} className="hideOnDesktop">
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M15.2886 4.00049L16 4.71451L8.45121 11.9993L16 19.2855L15.2885 19.9995L7 11.9992L15.2886 4.00049Z" />
                    </svg>
                    <BodyCopy as="div" desktopFont="subhead-xs">
                      {t('Back')}
                    </BodyCopy>
                  </Label>
                  {(modalSection == 'account' ||
                    modalSection == 'forgotPassword' ||
                    modalSection == 'createAccount') && (
                    <Label
                      onClick={() =>
                        toSetModalSection(
                          modalSection == 'forgotPassword' ? 'account' : 'accountIntro',
                        )
                      }
                      className="hideOnMobile"
                    >
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="M15.2886 4.00049L16 4.71451L8.45121 11.9993L16 19.2855L15.2885 19.9995L7 11.9992L15.2886 4.00049Z" />
                      </svg>
                      <BodyCopy as="div" desktopFont="subhead-sm">
                        {t('Back')}
                      </BodyCopy>
                    </Label>
                  )}
                </>
              )}
              <svg
                onClick={closeAllModals}
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ marginLeft: 0 }}
              >
                <path d="M14 2L2 14" stroke="#242323" strokeWidth="1.5" strokeLinecap="square" />
                <path
                  d="M14 14L2 2.00001"
                  stroke="#242323"
                  strokeWidth="1.5"
                  strokeLinecap="square"
                />
              </svg>
            </TopDefault>
          </div>
        }
        center={
          <List $gap={showInnerMobileSecondaryNav ? 'none' : 'lg'} $p="x8" $mobileP="xl">
            {modalSection == 'menu' && (
              <>
                {showInnerMobileSecondaryNav && mobileNav && secondaryNavData && (
                  <>
                    {/* Only showing first outer mobile nav link and linkList item as per styles */}
                    <InnerMobileSecondaryNavTopText>
                      <Link href={secondaryNavData.cta?.fields.url}>
                        {secondaryNavData.cta?.fields.text}
                      </Link>
                    </InnerMobileSecondaryNavTopText>

                    {secondaryNavData.linkList?.length && (
                      <InnerMobileSecondaryNavGrayText>
                        {secondaryNavData.linkList[0]!.fields?.text}
                      </InnerMobileSecondaryNavGrayText>
                    )}
                  </>
                )}

                {navMobileProps.map((item) => {
                  let { text, url, testId } = item.fields?.cta?.fields;
                  const dataTestId = item.fields.title
                    ?.trim()
                    ?.toLowerCase()
                    ?.split(' ')
                    ?.join('-');
                  const subLinks = item.fields.linkList;

                  // Remove the shop path from the URL
                  if (validSubdomainData?.type && validSubdomainData.subdomain) {
                    url = url.replace(`/shops/${validSubdomainData.subdomain}`, '');
                  }

                  // Apply domain override, if any
                  if (urlDomainOverride) {
                    url = `${urlDomainOverride}${url}`;
                  }

                  //check if sublinks exist
                  return (
                    <Details
                      key={item.sys.id}
                      $isInnerMobileSecondaryNav={showInnerMobileSecondaryNav}
                    >
                      <summary>
                        <Link
                          data-testid={dataTestId}
                          href={url}
                          target={item?.fields?.cta?.fields?.external ? '_blank' : '_self'}
                          onClick={
                            item?.fields?.cta?.fields?.external ? undefined : () => closeAllModals()
                          }
                        >
                          {text}
                        </Link>
                      </summary>
                      {typeof subLinks !== 'undefined' && subLinks.length > 0 && (
                        <ListDrop>
                          {subLinks.map((link) => {
                            let { text, url } = link.fields;

                            // Remove the shop path from the URL
                            if (validSubdomainData?.type && validSubdomainData.subdomain) {
                              url = url.replace(`/shops/${validSubdomainData.subdomain}`, '');
                            }

                            // Apply domain override, if any
                            if (urlDomainOverride) {
                              url = `${urlDomainOverride}${url}`;
                            }

                            return text !== t('Our Franchise') ? (
                              <Link
                                data-testid={testId}
                                href={url}
                                key={link.sys.id}
                                target={link?.fields?.external ? '_blank' : '_self'}
                                onClick={
                                  link?.fields?.external ? undefined : () => closeAllModals()
                                }
                              >
                                {text}
                              </Link>
                            ) : (
                              <Button
                                variant="primaryWhite"
                                label={text}
                                onClickHandler={() => setShowInnerMobileSecondaryNav(true)}
                                arrow={undefined}
                                justifyContent="flex-start"
                                alignItems="flex-start"
                                style={{
                                  padding: 0,
                                  fontWeight: 400,
                                  fontSize: '16px',
                                  marginLeft: '-0.1rem',
                                }}
                              />
                            );
                          })}
                        </ListDrop>
                      )}
                    </Details>
                  );
                })}
              </>
            )}
            {modalSection == 'welcomeMessage' && (
              <>
                <List>
                  <Headline $font="headline-xs" $p="15">
                    {`${t('Hello')},
                    ${user && user?.attributes?.given_name}`}
                  </Headline>
                  <Headline $font="subhead-md">
                    {siteModalData ? t(siteModalData.welcomeMessage) : t(`Your E&V Dashboard`)}
                  </Headline>
                  {welcomeMessageDescription && (
                    <RichText richText={welcomeMessageDescription as Document} />
                  )}
                </List>
              </>
            )}
            {modalSection == 'accountIntro' && (
              <>
                <List $gap="x7">
                  <Headline $font="headline-xs" $m="0">
                    {siteModalData ? siteModalData.headline : `Get the most out of E&V.`}
                  </Headline>
                  <List $gap="xl">
                    {siteModalData && siteModalData.richText ? (
                      <RichText richText={siteModalData.richText as Document} />
                    ) : (
                      <>
                        <Text>Create an E&V profile or log in to:</Text>
                        <UL>
                          <li>{t('Save your searches')}</li>
                          <li>{t('Keep track of your favorite properties')}</li>
                          <li>{t('Access exclusive market reports')}</li>
                        </UL>
                      </>
                    )}
                  </List>
                  {siteModalData && siteModalData.img ? (
                    <Img
                      src={siteModalData.img.fields.file.url}
                      alt="account intro"
                      loading="lazy"
                    />
                  ) : (
                    <Img src="/assets/accountIntro.jpg" alt="account intro" loading="lazy" />
                  )}
                </List>
              </>
            )}
            {modalSection == 'account' && (
              <>
                <List $gap="x7">
                  <Headline $font="headline-xs" $m="0">
                    {siteModalData ? siteModalData.accountLoginHeadline : t('Log In to My E&V')}
                  </Headline>

                  <List $gap="xl">
                    <List $gap="xl">
                      <List>
                        <Text>{t('Email Address')}</Text>
                        <InputField
                          placeholder="you@emailaddress.com"
                          value={username}
                          onChange={(event) => handleInputChange(event, setUsername)}
                        />
                      </List>
                      <List>
                        <Text>{t('Password')}</Text>
                        <InputField
                          type="password"
                          placeholder="password"
                          value={password}
                          onChange={(event) => handleInputChange(event, setPassword)}
                        />
                      </List>
                      {loginError && (
                        <List>
                          <Text $font="body-sm-light" color="darkred">
                            {loginError.msg}
                          </Text>
                        </List>
                      )}
                    </List>
                    <List $gap="lg">
                      <CustomStyledLink onClick={() => toSetModalSection('forgotPassword')}>
                        {t('Forgot Password')}
                      </CustomStyledLink>
                      {/* <button onClick={() => logout()}>LogOut</button> */}
                      <Label>
                        <CheckBox />
                        <BodyCopy as="div" desktopFont="body-sm-light" color="gray4">
                          {siteModalData.rememberMeCheckbox
                            ? siteModalData.rememberMeCheckbox
                            : t('Remember Me')}
                        </BodyCopy>
                      </Label>
                      <BodyCopy as="div" desktopFont="body-xxs-light" color="gray4">
                        {siteModalData.rememberMeDisclaimer
                          ? siteModalData.rememberMeDisclaimer
                          : `Warning: 'Remember Me' should only be used with your own
                        device or one assigned only to you. Never use the
                        feature on public or shared devices.`}
                      </BodyCopy>
                    </List>
                  </List>
                </List>
              </>
            )}
            {modalSection == 'verifyRegistration' && (
              <>
                <List $gap="x7">
                  <Headline $font="headline-xs" $m="0">
                    {siteModalData.verificationHeadline
                      ? siteModalData.verificationHeadline
                      : t('Verify Registration')}
                  </Headline>
                  <Text $font="body-sm-light" $color="grey4">
                    {siteModalData.verificationCopy ? (
                      <RichText richText={siteModalData.verificationCopy as Document} />
                    ) : (
                      t('verificationCodeMessage')
                    )}
                  </Text>
                </List>
                <InputField
                  placeholder="Enter confirmation code"
                  value={registrationCode}
                  onChange={(event) => handleInputChange(event, setRegistrationCode)}
                />
                {verifyCodeError && (
                  <Text $font="body-sm-light" $color="darkred">
                    {verifyCodeError.msg}
                  </Text>
                )}
              </>
            )}
            {modalSection == 'forgotPassword' && (
              <>
                <List $gap="x7">
                  <Headline $font="headline-xs" $m="0">
                    {siteModalData ? siteModalData.accountLoginHeadline : t('Log In to My E&V')}
                  </Headline>
                  <List $gap="xl">
                    <List>
                      <Text>{t('Email')}</Text>
                      <InputField
                        placeholder={'you@emailaddress.com'}
                        value={username}
                        disabled={isfpCodeSent}
                        onChange={(event) => handleInputChange(event, setUsername)}
                      />
                    </List>
                    {isfpCodeSent && (
                      <>
                        <List>
                          <Text>{t('Verification Code')}</Text>
                          <InputField
                            placeholder="123456"
                            value={fpVerificationCode}
                            onChange={(event) => handleInputChange(event, setfpVerificationCode)}
                          />
                        </List>
                        <List $gap="xs">
                          <Text $p="0">{t('Password')}</Text>
                          <InputField
                            placeholder={t('Password')}
                            type="password"
                            value={password}
                            onChange={(event) => handleInputChange(event, setPassword)}
                          />
                        </List>
                        <List $gap="xs">
                          <Text $p="0">{t('Confirm Password')}</Text>
                          <InputField
                            placeholder={t('Confirm Password')}
                            type="password"
                            value={confirmPassword}
                            onChange={(event) => handleInputChange(event, setConfirmPassword)}
                          />
                        </List>
                      </>
                    )}
                    {forgotPassError && (
                      <List>
                        <Text $font="body-sm-light" $color="darkred">
                          {forgotPassError}
                        </Text>
                      </List>
                    )}
                  </List>
                </List>
              </>
            )}
            {modalSection == 'createAccount' && (
              <>
                <Headline $font="headline-xs">
                  {siteModalData.createAccountHeadline
                    ? siteModalData.createAccountHeadline
                    : t('Create an E&V Profile')}
                </Headline>

                <List $gap="lg">
                  {siteModalData && siteModalData.createAccountCopy ? (
                    <RichText richText={siteModalData.createAccountCopy as Document} />
                  ) : (
                    <>
                      <Text>{t('With a profile you can:')}</Text>
                      <UL>
                        <li>{t('Save your searches')}</li>
                        <li>{t('Keep track of your favorite properties')}</li>
                        <li>{t('Access exclusive market reports')}</li>
                      </UL>
                    </>
                  )}
                </List>
                <br />
                <List $gap="xs">
                  <Text $p="0">{t('First Name')}</Text>
                  <InputField
                    placeholder={t('First Name')}
                    value={userFirstName}
                    onChange={(event) => handleInputChange(event, setUserFirstName)}
                    className={loginError && loginError.type == 'firstName' ? 'warning' : ''}
                  />
                  {loginError && loginError.type == 'firstName' && (
                    <Text $font="body-sm-light" $color="darkred">
                      ⓘ {loginError.msg}
                    </Text>
                  )}
                </List>
                <List $gap="xs">
                  <Text $p="0">{t('Last Name')}</Text>
                  <InputField
                    placeholder={t('Last Name')}
                    value={userLastName}
                    onChange={(event) => handleInputChange(event, setUserLastName)}
                    className={loginError && loginError.type == 'lastName' ? 'warning' : ''}
                  />
                  {loginError && loginError.type == 'lastName' && (
                    <Text $font="body-sm-light" $color="darkred">
                      ⓘ {loginError.msg}
                    </Text>
                  )}
                </List>
                <List $gap="xs">
                  <Text $p="0">{t('Phone')}</Text>
                  <InputField
                    placeholder="+14325551212"
                    value={userPhone}
                    onChange={(event) => handleInputChange(event, setUserPhone)}
                    className={loginError && loginError.type == 'phone' ? 'warning' : ''}
                  />
                  {loginError && loginError.type == 'phone' && (
                    <Text $font="body-sm-light" $color="darkred">
                      ⓘ {loginError.msg}
                    </Text>
                  )}
                </List>
                <List $gap="xs">
                  <Text $p="0">{t('Email')}</Text>
                  <InputField
                    placeholder={'you@emailaddress.com'}
                    value={userEmail}
                    onChange={(event) => handleInputChange(event, setUserEmail)}
                    className={loginError && loginError.type == 'email' ? 'warning' : ''}
                  />
                  {loginError && loginError.type == 'email' && (
                    <Text $font="body-sm-light" $color="darkred">
                      ⓘ {loginError.msg}
                    </Text>
                  )}
                </List>
                <List $gap="xs">
                  <Text $p="0">{t('Password')}</Text>
                  <InputField
                    placeholder={t('Password')}
                    type="password"
                    value={password}
                    onChange={(event) => handleInputChange(event, setPassword)}
                    className={loginError && loginError.type == 'password' ? 'warning' : ''}
                  />
                  {loginError && loginError.type == 'password' && (
                    <Text $font="body-sm-light" $color="darkred">
                      ⓘ {loginError.msg}
                    </Text>
                  )}
                </List>
                <List $gap="xs">
                  <Text $p="0">{t('Confirm Password')}</Text>
                  <InputField
                    placeholder={t('Confirm Password')}
                    type="password"
                    value={confirmPassword}
                    onChange={(event) => handleInputChange(event, setConfirmPassword)}
                    className={loginError && loginError.type == 'confirmPassword' ? 'warning' : ''}
                  />
                  {loginError && loginError.type == 'confirmPassword' && (
                    <Text $font="body-sm-light" $color="darkred">
                      ⓘ {loginError.msg}
                    </Text>
                  )}
                </List>
              </>
            )}
          </List>
        }
        bottom={
          <>
            {modalSection == 'menu' && (
              <>
                <PaddedList>
                  <ListCol
                    $gap="lg"
                    onClick={handleMobileLanguageClick}
                    style={{ userSelect: 'none' }}
                  >
                    <Text $p="0px">
                      {t('Language')}: {getLabelBySelectedOption(lang, languagesOptions)}
                    </Text>
                    <Text $p="0px">❯</Text>
                  </ListCol>
                  <ListCol
                    $gap="lg"
                    onClick={handleMobileCurrencyClick}
                    style={{ userSelect: 'none' }}
                  >
                    <Text $p="0px">
                      {t('Currency')}:{' '}
                      {getLabelBySelectedOption(preferredCurrency, allCurrenciesOptions)}
                    </Text>
                    <Text $p="0px">❯</Text>
                  </ListCol>
                  <ListCol
                    $gap="lg"
                    onClick={handleMobileMeasurementClick}
                    style={{ userSelect: 'none' }}
                  >
                    <Text $p="0px">
                      {t('Measurement')}:{' '}
                      {getLabelBySelectedOption(preferredMeasurement, measurementOptions)}
                    </Text>
                    <Text $p="0px">❯</Text>
                  </ListCol>
                </PaddedList>
              </>
            )}
            <PaddedShadowList>
              {modalSection == 'welcomeMessage' && (
                <>
                  <Button
                    variant="button"
                    label={t('View My Dashboard')}
                    arrow={undefined}
                    url={'/dashboard/account-menu'}
                  />
                  <Button
                    variant="secondary"
                    label={t('Log Out')}
                    arrow={undefined}
                    onClickHandler={() => logout()}
                  />
                </>
              )}
              {modalSection == 'menu' &&
                (user ? (
                  <>
                    <Button
                      variant="button"
                      label={t('View My Dashboard')}
                      arrow={undefined}
                      onClickHandler={() => redirectToDashbaord()}
                    />
                    <Button
                      variant="secondary"
                      label={t('Log Out')}
                      arrow={undefined}
                      onClickHandler={() => logout()}
                    />
                  </>
                ) : (
                  <>
                    <Button
                      variant="button"
                      label={t('Log In')}
                      arrow={undefined}
                      onClickHandler={() =>
                        !isObjectEmpty(user)
                          ? toSetModalSection('welcomeMessage')
                          : toSetModalSection('account')
                      }
                    />
                    <Button
                      variant="secondary"
                      label={t('Register')}
                      arrow={undefined}
                      onClickHandler={() => toSetModalSection('createAccount')}
                    />
                  </>
                ))}
              {modalSection == 'accountIntro' && (
                <>
                  <Button
                    variant="button"
                    label={t('Log In')}
                    arrow={undefined}
                    onClickHandler={() => toSetModalSection('account')}
                  />
                  <Button
                    variant="secondary"
                    label={t('Register')}
                    arrow={undefined}
                    onClickHandler={() => toSetModalSection('createAccount')}
                  />
                </>
              )}
              {modalSection == 'account' && (
                <>
                  <Button
                    variant="button"
                    label={t('Log In')}
                    arrow={undefined}
                    onClickHandler={() => login()}
                  />
                  {/* <Button
                    variant="secondary"
                    label={t('Continue with Google')}
                    arrow={undefined}
                    icon={'GoogleColor'}
                    iconSize="20px"
                    iconPosition="left"
                    onClickHandler={() => auth.signInWithGoogle()}
                  /> */}
                  {/* <Button
                    variant="secondary"
                    label="Continue with Facebook"
                    arrow={undefined}
                    icon={'FacebookColor'}
                    iconPosition="left"
                    onClickHandler={() => auth.signInWithFacebook()}
                  /> */}
                </>
              )}
              {modalSection == 'verifyRegistration' && (
                <>
                  <List $gap="sm">
                    <Button
                      variant="button"
                      label={t('Verify Email')}
                      arrow={undefined}
                      onClickHandler={verifyRegistrationCode}
                    />
                    <Button
                      variant="secondary"
                      label={t('Resend Code')}
                      arrow={undefined}
                      onClickHandler={resendRegistrationCode}
                    />
                  </List>
                </>
              )}
              {modalSection == 'forgotPassword' && (
                <>
                  {isfpCodeSent ? (
                    <Button
                      variant="button"
                      label={t('Reset Password')}
                      arrow={undefined}
                      onClickHandler={() => resetPasswordWithVerificationCode()}
                    />
                  ) : (
                    <>
                      <Button
                        variant="button"
                        label={t('Send Verification Code')}
                        arrow={undefined}
                        onClickHandler={() => sendForgotPasswordCode()}
                      />
                    </>
                  )}
                </>
              )}

              {modalSection == 'createAccount' && (
                <>
                  {loginError && loginError.type == 'unknown' && (
                    <List>
                      <Text $font="body-sm-light" $color="darkred">
                        {loginError.msg}
                      </Text>
                    </List>
                  )}
                  <Button
                    variant="button"
                    label={t('Register')}
                    arrow={undefined}
                    onClickHandler={() => registerUser()}
                  />
                  {/* <Button
                    variant="secondary"
                    label="Continue with Google"
                    arrow={undefined}
                    icon={'GoogleColor'}
                    iconSize="20px"
                    iconPosition="left"
                  /> */}
                  {/* <Button
                    variant="secondary"
                    label="Continue with Facebook"
                    arrow={undefined}
                    icon={'FacebookColor'}
                    iconPosition="left"
                  /> */}
                </>
              )}
            </PaddedShadowList>
          </>
        }
        ref={modal}
        onClose={() =>
          setTimeout(() => {
            toSetModalSection('menu');
          }, 500)
        } // wait for modal to close
      />
      <Modal
        top={
          <div>
            <TopDefault>
              <svg
                onClick={closeAllModals}
                width="16"
                height="15"
                viewBox="0 0 16 15"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                data-testid="close-language-modal"
              >
                <path d="M8.0001 8.7055L14.2966 14.9988L15.0037 14.292L8.7072 7.9988L15.0037 1.70553L14.2966 0.998779L8.0001 7.292L1.70369 0.998779L0.996582 1.70553L7.293 7.9988L0.996582 14.292L1.70369 14.9988L8.0001 8.7055Z" />
              </svg>
            </TopDefault>
          </div>
        }
        center={
          <List $gap="lg" $p="x8" $mobileP="xl">
            <Headline data-testid="language-currency-header" $font="headline-xs">
              {t('Language & Currency')}
            </Headline>
            <div>
              <Text data-testid="preferred-language" $p="0 0 4px 0" $font="body-md">
                {t('Preferred Language')}
              </Text>
              <CustomDropdown
                key={lang}
                value={lang}
                onSelect={(val) => changeLang(val)}
                testIdType="language"
                options={languagesOptions}
              />
            </div>
            <div>
              <Text data-testid="preferred-currency" $p="0 0 4px 0" $font="body-md">
                {t('Preferred Currency')}
              </Text>
              <CustomDropdown
                value={preferredCurrency}
                onSelect={setPreferredCurrency}
                testIdType="currency"
                options={allCurrenciesOptions}
              />
            </div>
            <div>
              <Text $p="0 0 4px 0" $font="body-md">
                {t('Preferred Measurement')}
              </Text>
              <CustomDropdown
                value={preferredMeasurement}
                onSelect={(val) => setPreferredMeasurement(val as MeasurementUnit)}
                testIdType="measurement"
                options={measurementOptions}
              />
            </div>
          </List>
        }
        bottom={<></>}
        ref={languageModal}
      />
      <Modal
        top={
          <div>
            <TopDefault>
              <Label onClick={handleMobileLanguageClick}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M15.2886 4.00049L16 4.71451L8.45121 11.9993L16 19.2855L15.2885 19.9995L7 11.9992L15.2886 4.00049Z"
                    fill="#242323"
                  />
                </svg>
                <BodyCopy as="div" desktopFont="subhead-xs">
                  {t('Back')}
                </BodyCopy>
              </Label>
              <svg
                onClick={closeAllModals}
                width="16"
                height="15"
                viewBox="0 0 16 15"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M8.0001 8.7055L14.2966 14.9988L15.0037 14.292L8.7072 7.9988L15.0037 1.70553L14.2966 0.998779L8.0001 7.292L1.70369 0.998779L0.996582 1.70553L7.293 7.9988L0.996582 14.292L1.70369 14.9988L8.0001 8.7055Z" />
              </svg>
            </TopDefault>
          </div>
        }
        center={
          <List $gap="lg" $p="x8" $mobileP="xl">
            <Headline $font="headline-xs">{t('Language')}</Headline>
            <div>
              <Text $p="0 0 4px 0">{t('Preferred Language')}</Text>
              <CustomDropdown
                value={lang}
                onSelect={(val) => changeLang(val)}
                options={languagesOptions}
                testIdType="language"
                open={true}
              />
            </div>
          </List>
        }
        bottom={<></>}
        ref={mobileLanguageModal}
      />
      <Modal
        top={
          <div>
            <TopDefault>
              <Label onClick={handleMobileCurrencyClick}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M15.2886 4.00049L16 4.71451L8.45121 11.9993L16 19.2855L15.2885 19.9995L7 11.9992L15.2886 4.00049Z"
                    fill="#242323"
                  />
                </svg>
                <BodyCopy as="div" desktopFont="subhead-xs">
                  {t('Back')}
                </BodyCopy>
              </Label>
              <svg
                onClick={closeAllModals}
                width="16"
                height="15"
                viewBox="0 0 16 15"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M8.0001 8.7055L14.2966 14.9988L15.0037 14.292L8.7072 7.9988L15.0037 1.70553L14.2966 0.998779L8.0001 7.292L1.70369 0.998779L0.996582 1.70553L7.293 7.9988L0.996582 14.292L1.70369 14.9988L8.0001 8.7055Z" />
              </svg>
            </TopDefault>
          </div>
        }
        center={
          <List $gap="lg" $p="x8" $mobileP="xl">
            <Headline $font="headline-xs">{t('Currency')}</Headline>
            <div>
              <Text $p="0 0 4px 0">{t('Preferred Currency')}</Text>
              <CustomDropdown
                value={preferredCurrency}
                onSelect={setPreferredCurrency}
                options={allCurrenciesOptions}
                testIdType="currency"
                open={true}
              />
            </div>
          </List>
        }
        bottom={<></>}
        ref={mobileCurrencyModal}
      />
      <Modal
        top={
          <div>
            <TopDefault>
              <Label onClick={handleMobileMeasurementClick}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M15.2886 4.00049L16 4.71451L8.45121 11.9993L16 19.2855L15.2885 19.9995L7 11.9992L15.2886 4.00049Z"
                    fill="#242323"
                  />
                </svg>
                <BodyCopy as="div" desktopFont="subhead-xs">
                  {t('Back')}
                </BodyCopy>
              </Label>
              <svg
                onClick={closeAllModals}
                width="16"
                height="15"
                viewBox="0 0 16 15"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M8.0001 8.7055L14.2966 14.9988L15.0037 14.292L8.7072 7.9988L15.0037 1.70553L14.2966 0.998779L8.0001 7.292L1.70369 0.998779L0.996582 1.70553L7.293 7.9988L0.996582 14.292L1.70369 14.9988L8.0001 8.7055Z" />
              </svg>
            </TopDefault>
          </div>
        }
        center={
          <List $gap="lg" $p="x8" $mobileP="xl">
            <Headline $font="headline-xs">{t('Measurement')}</Headline>
            <div>
              <Text $p="0 0 4px 0">{t('Preferred Measurement')}</Text>
              <CustomDropdown
                value={preferredMeasurement}
                onSelect={(val) => setPreferredMeasurement(val as MeasurementUnit)}
                options={measurementOptions}
                testIdType="measurement"
                open={true}
              />
            </div>
          </List>
        }
        bottom={<></>}
        ref={mobileMeasurementModal}
      />
      <Header>
        <Logo dataTestId="global-site-header-logo" />
        <Column>
          {nav.map((item) => {
            let { text, url, testId } = item.fields.cta.fields;
            const dataTestId = item.fields.title?.trim()?.toLowerCase()?.split(' ')?.join('-');
            if (url.startsWith('/') && lang != 'en' && lang.toLocaleLowerCase() != 'en-us')
              url = `/${lang}${url}`;
            const subLinks = item.fields.linkList;

            // Remove the shop path from the URL
            if (validSubdomainData?.type && validSubdomainData.subdomain) {
              url = url.replace(`/shops/${validSubdomainData.subdomain}`, '');
            }

            // Apply domain override, if any
            if (urlDomainOverride) {
              url = `${urlDomainOverride}${url}`;
            }
            // if (currentAppSubdomain) {
            //   url = url
            //     .replace(`/shops/${currentAppSubdomain}`, '')
            //     .replace(`/shops/${currentAdvisorShopSubdomain}`, '');
            //   const mProtocol = rewriteBaseUrl.includes('localhost') ? 'http://' : 'https://';
            //   if (rewriteBaseUrl) {
            //     url = `${mProtocol}${rewriteBaseUrl}${url}`;
            //   }
            // }

            const handleLinkClick = async (text) => {
              // Only redirect if nav text is 'Buy' or 'Rent'
              if (text !== 'Buy' && text !== 'Rent') return;

              const urlWithListingTypes =
                text === 'Rent'
                  ? `${url}?listingTypes=For+Rent`
                  : text === 'Buy'
                  ? `${url}?listingTypes=For+Sale`
                  : url;

              // Only redirect if on the properties page
              if (pathname.includes('properties') && !pathname.includes('our-listings')) {
                window.location.href = urlWithListingTypes;
              } else {
                return;
              }
            };

            return (
              <Dropdown className="hideOnMobile" key={item.sys.id}>
                <Link
                  data-testid={dataTestId}
                  href={url}
                  target={item?.fields?.cta?.fields?.external ? '_blank' : '_self'}
                >
                  {text}
                </Link>
                {typeof subLinks !== 'undefined' && subLinks.length > 0 && (
                  <div className="menu" data-testid={`${dataTestId}-menu`}>
                    {subLinks.map((link) => {
                      let { text, url } = link.fields;
                      const sublinkTestId = `${text
                        ?.trim()
                        ?.toLowerCase()
                        ?.split(' ')
                        ?.join('-')}-sublink`;

                      if (
                        url.startsWith('/') &&
                        lang != 'en' &&
                        lang.toLocaleLowerCase() != 'en-us'
                      ) {
                        url = `/${lang}${url}`;
                      }

                      // Remove the shop path from the URL
                      if (validSubdomainData?.type && validSubdomainData.subdomain) {
                        url = url.replace(`/shops/${validSubdomainData.subdomain}`, '');
                      }

                      // Apply domain override, if any
                      if (urlDomainOverride) {
                        url = `${urlDomainOverride}${url}`;
                      }

                      return (
                        <Link
                          data-testid={sublinkTestId}
                          href={url}
                          key={link.sys.id}
                          target={link?.fields?.external ? '_blank' : '_self'}
                          onClick={() => handleLinkClick(text)}
                        >
                          {text}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </Dropdown>
            );
          })}
          <Column>
            <div></div>
            <MenuIcon
              data-testid={'language-and-currency-btn'}
              onClick={handleLanguageClick}
              className="hideOnMobile"
            >
              <svg
                className="icon"
                width="20"
                height="21"
                viewBox="0 0 20 21"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M9.89577 1.51934C7.40749 3.81837 5.8335 7.21313 5.8335 11C5.8335 14.7274 7.35845 18.0749 9.77925 20.3716"
                  stroke="#757575"
                  strokeWidth="1.5"
                />
                <path
                  d="M10.1635 20.4256C12.6175 18.1276 14.1665 14.7567 14.1665 11C14.1665 7.24329 12.6175 3.87245 10.1635 1.57446"
                  stroke="#757575"
                  strokeWidth="1.5"
                />
                <circle cx="10" cy="11" r="9.25" stroke="#757575" strokeWidth="1.5" />
                <path d="M1.25 7.66666H19.1667" stroke="#757575" strokeWidth="1.5" />
                <path d="M1.25 14.3333H19.1667" stroke="#757575" strokeWidth="1.5" />
              </svg>
            </MenuIcon>
            <MenuIcon
              data-testid={'user-account-open-btn'}
              onClick={() =>
                openModalSection(!isObjectEmpty(user) ? 'welcomeMessage' : 'accountIntro')
              }
              className="hideOnMobile"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="10.0002" cy="7.50001" r="3.41667" stroke="#757575" strokeWidth="1.5" />
                <path
                  d="M3.66211 16.8831C4.93423 14.7598 7.29603 13.3333 9.99942 13.3333C12.7028 13.3333 15.0646 14.7598 16.3367 16.8831"
                  stroke="#757575"
                  strokeWidth="1.5"
                />
                <circle cx="10" cy="10" r="9.25" stroke="#757575" strokeWidth="1.5" />
              </svg>
            </MenuIcon>
          </Column>
          <MenuIcon onClick={handleClick} className="hideOnDesktop">
            <svg
              width="22"
              height="16"
              viewBox="0 0 22 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M1 1H21" stroke="#242323" strokeWidth="1.5" strokeLinecap="square" />
              <path d="M1 8H21" stroke="#242323" strokeWidth="1.5" strokeLinecap="square" />
              <path d="M1 15H21" stroke="#242323" strokeWidth="1.5" strokeLinecap="square" />
            </svg>
          </MenuIcon>
        </Column>
      </Header>
    </>
  );
});

export default GlobalSiteHeader;

/*** STYLING ***/
const Header = styled.header`
  display: grid;
  grid-gap: 12px;
  grid-auto-flow: column;
  justify-content: space-between;
  align-items: center;
  padding-top: 40px;
  height: 64px;
  margin: auto;
  width: calc(100% - ${space('x12')} - ${space('x12')});
  max-width: 1272px;
  border-bottom: 1px solid ${color('gray2')};
  background-color: white;
  position: relative;
  z-index: 100;
  .hideOnDesktop {
    display: none;
  }
  @media screen and (${query.below('desktop')}) {
    width: calc(100% - ${space('marginTablet')} - ${space('marginTablet')});
    padding-top: 10px;
  }
  @media screen and (${query.below('tablet')}) {
    width: calc(100% - ${space('marginMobile')} - ${space('marginMobile')});
    padding-top: unset;
  }
  @media (max-width: 900px) {
    .hideOnMobile {
      display: none;
    }
    .hideOnDesktop {
      display: inherit;
      padding: unset;
    }
  }
`;

const PaddedShadowList = styled.div`
  display: grid;
  grid-gap: 16px;
  justify-items: stretch;
  box-shadow: 0px 1px 41px rgba(0, 0, 0, 0.1);
  padding: ${space('xl')} ${space('x5')};
  @media screen and (${query.below('tablet')}) {
    padding: ${space('x5')};
  }

  button {
    font-size: 14px;
  }
`;

const Label = styled.label`
  display: grid;
  justify-content: start;
  align-items: center;
  grid-auto-flow: column;
  user-select: none;
  cursor: pointer;
  gap: ${space('sm')};
  @media screen and (${query.atLeast('tablet')}) {
    &.hideOnDesktop {
      display: none;
    }
  }
  @media screen and (${query.below('tablet')}) {
    &.hideOnMobile {
      display: none;
    }
  }
`;

const PaddedList = styled.div`
  display: grid;
  grid-gap: 16px;
  justify-items: stretch;
  padding: ${space('x8')};
  @media screen and (${query.below('tablet')}) {
    padding: ${space('x5')};
  }
`;

const UL = styled.ul`
  list-style: disc;
  padding-left: ${space('x5')};

  li {
    font-weight: 300;
  }
`;

const Img = styled.img`
  width: 80%;
  margin: auto;
`;

const Select = styled.select`
  padding: 8px 16px;
  border-radius: 5px;
  border: 1px solid #d4d4d4;
  font-size: 16px;
  margin-bottom: 16px;
`;

const ArrowLink = styled.div`
  user-select: none;
  cursor: pointer;
  position: relative;
  height: 40px;
  display: grid;
  align-items: center;
  padding-right: 16px;
  transition: 0.2s;

  &:after {
    content: '❯';
    position: absolute;
    margin-right: 16px;
    right: calc(16px / 2);
    top: 50%;
    transform: translateY(-50%) translateX(50%);
    transition: 0.3s;
    transition-timing-function: ease-in-out;
  }
`;

const SelectDetails = styled.details`
  border-radius: 5px;
  border: 1px solid #d4d4d4;
  font-size: 16px;
  margin-bottom: 16px;

  summary {
    list-style: none;
    user-select: none;
    cursor: pointer;
    position: relative;
    height: 40px;
    display: grid;
    align-items: center;
    padding: 0px 16px;
    transition: 0.2s;
    &::-webkit-details-marker {
      display: none;
    }
  }
  summary:after {
    content: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="9" height="16" fill="none"><path fill="%23242323" d="M8.289 0 9 .715 1.451 7.999 9 15.286l-.711.713L0 8 8.289 0Z"/></svg>');
    position: absolute;
    margin-right: 16px;
    right: calc(16px / 2);
    top: 50%;
    transform: translateY(-50%) translateX(50%) rotate(-90deg);
    transition: 0.3s;
    transition-timing-function: ease-in-out;
    appearance: none;
  }
  &[open] summary:after {
    transform: translateY(-50%) translateX(50%) rotate(90deg);
  }
  &[open] summary {
    background: #f4f4f4;
    border-bottom: 1px solid #d4d4d4;
  }
  .options {
    max-height: 400px;
    overflow: auto;
    padding: 4px 0;
    height: calc(100vh - 300px);
  }
  .options > * {
    ${font('body-sm-light')};
    padding: 7px 16px;
    user-select: none;
    cursor: pointer;
    transition: 0.2s;
  }
  .options > *:hover {
    background: #f4f4f4;
  }
`;

const Details = styled.details<{
  $isInnerMobileSecondaryNav: boolean;
}>`
  --size: 19px;
  --marginRight: 16px;
  border-bottom: ${({ $isInnerMobileSecondaryNav }) =>
    $isInnerMobileSecondaryNav ? 'none' : '1px solid #d4d4d4'};
  summary {
    list-style: none;
    user-select: none;
    cursor: pointer;
    position: relative;
    height: ${({ $isInnerMobileSecondaryNav }) => ($isInnerMobileSecondaryNav ? '44px' : '62px')};
    display: grid;
    align-items: center;
    font-weight: ${({ $isInnerMobileSecondaryNav }) =>
      $isInnerMobileSecondaryNav ? '400' : 'bold'};
    justify-content: start;
    &::-webkit-details-marker {
      display: none;
    }
  }

  summary:before {
    content: ${({ $isInnerMobileSecondaryNav }) => ($isInnerMobileSecondaryNav ? 'none' : "''")};
    position: ${({ $isInnerMobileSecondaryNav }) =>
      $isInnerMobileSecondaryNav ? 'static' : 'absolute'};
    margin-right: ${({ $isInnerMobileSecondaryNav }) =>
      $isInnerMobileSecondaryNav ? '0' : 'var(--marginRight)'};
    right: ${({ $isInnerMobileSecondaryNav }) => ($isInnerMobileSecondaryNav ? 'auto' : '0')};
    top: ${({ $isInnerMobileSecondaryNav }) => ($isInnerMobileSecondaryNav ? 'auto' : '50%')};
    transform: ${({ $isInnerMobileSecondaryNav }) =>
      $isInnerMobileSecondaryNav ? 'none' : 'translateY(-50%)'};
    width: ${({ $isInnerMobileSecondaryNav }) =>
      $isInnerMobileSecondaryNav ? '0' : 'var(--size)'};
    height: ${({ $isInnerMobileSecondaryNav }) => ($isInnerMobileSecondaryNav ? '0' : '1px')};
    background-color: ${({ $isInnerMobileSecondaryNav }) =>
      $isInnerMobileSecondaryNav ? 'transparent' : 'black'};
    transition: 0.3s;
  }
  summary:after {
    content: ${({ $isInnerMobileSecondaryNav }) => ($isInnerMobileSecondaryNav ? 'none' : "''")};
    position: ${({ $isInnerMobileSecondaryNav }) =>
      $isInnerMobileSecondaryNav ? 'static' : 'absolute'};
    margin-right: ${({ $isInnerMobileSecondaryNav }) =>
      $isInnerMobileSecondaryNav ? '0' : 'var(--marginRight)'};
    right: ${({ $isInnerMobileSecondaryNav }) =>
      $isInnerMobileSecondaryNav ? '0' : 'calc(var(--size) / 2)'};
    top: ${({ $isInnerMobileSecondaryNav }) => ($isInnerMobileSecondaryNav ? 'auto' : '50%')};
    transform: ${({ $isInnerMobileSecondaryNav }) =>
      $isInnerMobileSecondaryNav ? 'none' : 'translateY(-50%) translateX(50%)'};
    width: ${({ $isInnerMobileSecondaryNav }) => ($isInnerMobileSecondaryNav ? '0' : '1px')};
    height: ${({ $isInnerMobileSecondaryNav }) =>
      $isInnerMobileSecondaryNav ? '0' : 'var(--size)'};
    background-color: ${({ $isInnerMobileSecondaryNav }) =>
      $isInnerMobileSecondaryNav ? 'transparent' : 'black'};
    transition: 0.3s;
    transition-timing-function: ease-in-out;
    border: ${({ $isInnerMobileSecondaryNav }) =>
      $isInnerMobileSecondaryNav ? 'none' : '1px solid white'};
  }
  &[open] summary:after {
    transform: translateY(-50%) translateX(50%) rotate(90deg);
  }

  a {
    color: currentColor;
    text-decoration: none;
  }
`;

const SimpleAccordian = styled.details`
  --size: 19px;
  --marginRight: 16px;

  > summary {
    user-select: none;
    cursor: pointer;
    position: relative;
    display: grid;
    align-items: center;
    grid-auto-flow: column;
    justify-content: start;
    grid-gap: 14px;
  }
  > summary:after {
    content: '❯';
    transition: 0.3s;
    transition-timing-function: ease-in-out;
  }
  &[open] > summary {
    margin-bottom: 16px;
  }
  &[open] > summary:after {
    transform: rotate(90deg);
  }
`;

const ListDrop = styled.div`
  display: grid;
  grid-gap: 10px;
  padding-bottom: 24px;
`;

const MenuIcon = styled.div`
  padding: 12px;
  cursor: pointer;

  svg {
    path {
      transition: 0.3s;
    }
    circle {
      transition: 0.3s;
    }
  }
  svg:hover {
    path {
      stroke: black;
    }
    circle {
      stroke: black;
    }
  }
`;

const MainMenuText = styled.div`
  font-size: 16px;
  line-height: 28px;
  letter-spacing: 0.48px;
`;

const TopDefault = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 28px 20px;
  box-sizing: border-box;
  svg {
    fill: rgba(0, 0, 0, 0.65);
    transition: 0.3s;
    cursor: pointer;
    margin-left: auto;
  }
  svg:hover {
    fill: black;
  }
  @media screen and (${query.atLeast('desktop')}) {
    padding: 28px ${space('x5')};
  }
`;

const Column = styled.div`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  grid-gap: 0px;
  position: relative;
  height: 100%;
  z-index: 25;
  a,
  > .icon {
    height: 100%;
    line-height: 1.1;
    text-decoration: none;
    display: grid;
    align-items: center;
    padding: 0 ${space('xl')};
    color: #757575;
    transition: 0.25s;

    @media screen and (${query.atLeast('max')}) {
      padding: 0 ${space('x5')};
    }
  }
  a:hover {
    color: #242323;
  }
`;

const Dropdown = styled.div`
  display: grid;
  position: relative;
  height: 100%;
  z-index: 10;
  &:hover .menu {
    display: grid;
  }
  .menu {
    background-color: white;
    display: none;
    position: absolute;
    top: 100%;
    left: 0px;
    width: 192px;
    box-shadow: 2px 4px 15px rgba(0, 0, 0, 0.05);
    border-radius: 0px 0px 4px 4px;
    border-top: 1px solid ${color('gray2')};
  }
  .menu a:first-child {
    margin-top: 1px;
  }
  .menu a {
    height: 44px;
    background-color: white;
  }
  .menu a:hover {
    background-color: #f4f4f4;
  }
`;

const CustomStyledLink = styled.div`
  ${font('subhead-xs')};
  color: ${color('gray4')};
  text-decoration: underline;
  text-underline-offset: 4px;
  cursor: pointer;
  a:visited {
    color: initial;
  }
`;

const OptionsList = styled.div<{ $isActive: boolean }>`
  background-color: ${({ $isActive }) => ($isActive ? color('gray2') : 'none')};
`;

const InnerMobileSecondaryNavGrayText = styled.div`
  color: #666;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px; /* 166.667% */
  letter-spacing: 2px;
  text-transform: uppercase;
  margin-bottom: 16px;
`;

const InnerMobileSecondaryNavTopText = styled.div`
  margin-bottom: 24px;

  a {
    font-weight: 700;
    line-height: 22px;
    text-decoration: none;
    color: #242323;
  }
`;
